import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useAuth } from "providers/auth";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import {
  LEARNER_REGISTER_FOR_GROUP_SESSION,
  LEARNER_UNREGISTER_FOR_GROUP_SESSION,
  LEARNER_JOIN_GROUP_SESSION,
} from "utility/graphQl/mutation";

import appInfo from "utility/app-info";
import { LearnerSessionCardWebPartsFragment } from "../api/types";

import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { useToast } from "components/ui";

const createErrorMessage = (action: string) =>
  `Could not ${action}. Please try again. If the problem persists, contact support@walkingonearth.com`;

const TOAST_MESSAGES = {
  RESERVATION_SUCCESS: {
    messageHeader: "Successfully reserved",
    message:
      "You'll soon receive a confirmation email, including a calendar invite to the event.",
  },
  CANCELLATION_SUCCESS: {
    messageHeader: "Cancelled reservation",
    message:
      "Your spot has been successfully cancelled. You'll receive a confirmation email shortly.",
  },
  RESERVATION_ERROR: {
    messageHeader: "Booking failed",
    message: createErrorMessage("book this session"),
  },
  CANCELLATION_ERROR: {
    messageHeader: "Cancellation failed",
    message: createErrorMessage("cancel your booking"),
  },
  JOIN_ERROR: {
    messageHeader: "Join failed",
    message: createErrorMessage("join this session"),
  },
} as const;

const GROUP_SESSION_TYPENAME = "LearnerGroupSession";

interface Props {
  session: LearnerSessionCardWebPartsFragment;
  utmParameters?: {
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    utmSource?: string;
  };
}

export const useGroupSessionActions = ({ session, utmParameters }: Props) => {
  const navigate = useNavigate();
  const { learner } = useAuth();
  const { openToast } = useToast();

  const utmParametersCheck =
    utmParameters?.utmMedium ||
    utmParameters?.utmCampaign ||
    utmParameters?.utmTerm ||
    utmParameters?.utmContent ||
    utmParameters?.utmSource;

  const showToast = (messageType: keyof typeof TOAST_MESSAGES) => {
    const message = TOAST_MESSAGES[messageType];
    openToast({
      messageHeader: message.messageHeader,
      message: message.message,
      size: "small",
    });
  };

  const [register, { loading: isRegistering }] = useMutation(
    LEARNER_REGISTER_FOR_GROUP_SESSION,
    {
      variables: {
        id: session?.id,
        appInfo,
        utmParameters: utmParametersCheck ? utmParameters : null,
      },
      onCompleted: () => {
        showToast("RESERVATION_SUCCESS");
      },
      onError: (error) => {
        showToast("RESERVATION_ERROR");
        logError("Error registering for a group session", error);
      },
    }
  );

  const [unRegister, { loading: isUnRegistering }] = useMutation(
    LEARNER_UNREGISTER_FOR_GROUP_SESSION,
    {
      variables: {
        id: session?.id,
        appInfo,
      },
      onCompleted: () => {
        showToast("CANCELLATION_SUCCESS");
      },
      onError: (error) => {
        showToast("CANCELLATION_ERROR");
        logError("Error unregistering for a group session", error);
      },
    }
  );

  const [join, { loading: isJoining }] = useMutation(
    LEARNER_JOIN_GROUP_SESSION,
    {
      variables: {
        id: session?.id,
        appInfo,
        utmParameters: utmParametersCheck ? utmParameters : null,
      },
    }
  );

  const handleClickRegisterForGroupSession = (listName, listPosition) => {
    if (session?.__typename !== GROUP_SESSION_TYPENAME) {
      throw new Error(
        `Invalid session type "${session?.__typename}". Expected "${GROUP_SESSION_TYPENAME}"`
      );
    }

    trackEvent("Live Session Register Clicked", {
      "Session ID": session?.id,
      "Session Title": session?.title,
      "List Name": listName,
      "List Position": listPosition,
    });

    logAnalyticsEvent("group_session_register_click", {
      user_id: learner.id,
      user_id_wone: learner.id,
      user_company_name: learner.companyName,
      group_session_instance_id: session?.id,
      group_session_name: session?.title,
      list_name: listName,
      list_position: listPosition,
      page_title: document.title,
      page_path: window.location.pathname,
    });

    register();
  };

  const handleClickUnRegisterForGroupSession = (listName, listPosition) => {
    if (session?.__typename !== GROUP_SESSION_TYPENAME) {
      throw new Error(
        `Invalid session type "${session?.__typename}". Expected "${GROUP_SESSION_TYPENAME}"`
      );
    }

    trackEvent("Live Session Unregister Clicked", {
      "Session ID": session?.id,
      "Session Title": session?.title,
      "List Name": listName,
      "List Position": listPosition,
    });

    logAnalyticsEvent("group_session_unregister_click", {
      user_id: learner.id,
      user_id_wone: learner.id,
      user_company_name: learner.companyName,
      group_session_instance_id: session?.id,
      group_session_name: session?.title,
      list_name: listName,
      list_position: listPosition,
      page_title: document.title,
      page_path: window.location.pathname,
    });

    unRegister();
  };

  const handleClickJoinGroupSession = (listName, listPosition) => {
    if (session?.__typename !== GROUP_SESSION_TYPENAME) {
      throw new Error(
        `Invalid session type "${session?.__typename}". Expected "${GROUP_SESSION_TYPENAME}"`
      );
    }

    trackEvent("Live Session Join Clicked", {
      "Session ID": session?.id,
      "Session Title": session?.title,
      "List Name": listName,
      "List Position": listPosition,
    });

    logAnalyticsEvent("group_session_join_click", {
      user_id: learner.id,
      user_id_wone: learner.id,
      user_company_name: learner.companyName,
      group_session_instance_id: session?.id,
      group_session_name: session?.title,
      list_name: listName,
      list_position: listPosition,
      page_title: document.title,
      page_path: window.location.pathname,
    });

    join()
      .then((response) => {
        const {
          zoomMtgNumber,
          zoomMtgPassWord,
          zoomMtgSignature,
          learnerBeforeGroupSessionFeedbackForm,
        } = response.data.learnerJoinGroupSession;

        const learnerEmail = localStorage.getItem("userEmailId");

        localStorage.setItem(
          "teacherfirstName",
          session?.practitioner.firstName
        );

        navigate(`/user/sessions/group/${session?.id}/meeting`, {
          state: {
            title: `Meeting - ${session?.title} - Sessions`,
            sessionName: session?.title,
            sessionType: "GROUP",
            userId: learner.id,
            sessionId: session?.id,
            activeSessionName: "group",
            teacherId: session?.practitioner.id,
            activeStudentName: learner.fullName,
            activeTeacherName: session?.practitioner.fullName,
            activeUserEmail: learnerEmail,
            listName,
            listPosition,
            zoomMtgSignature,
            zoomMtgPassWord,
            zoomMtgNumber,
            learnerBeforeGroupSessionFeedbackForm:
              learnerBeforeGroupSessionFeedbackForm,
          },
        });
      })
      .catch((err) => {
        logError("Error joining a group session", err);
        showToast("JOIN_ERROR");
      });
  };

  const actions = {
    REGISTER: {
      loading: isRegistering,
      action: handleClickRegisterForGroupSession,
    },
    UNREGISTER: {
      loading: isUnRegistering,
      action: handleClickUnRegisterForGroupSession,
    },
    JOIN: {
      loading: isJoining,
      action: handleClickJoinGroupSession,
    },
  };

  return actions;
};
